import {
  useWSModal,
  WSForm,
  WSInputDate,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { DeductionDeductionType } from "@wingspanhq/wingspan-api-sdk";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { SelectPayee } from "../../../../modules/Payees/components/SelectPayee";
import { queriesDeductions } from "@query";

type Props = {
  onClose: () => void;
};

export const AddPayeeDeduction: React.FC<Props> = ({ onClose }) => {
  const [createDeduction, createDeductionMeta] =
    queriesDeductions.createDeduction.useMutation();

  return (
    <WSForm<{
      name: string;
      amount: number;
      payeeId: string;
      detail: string;
    }>
      defaultValues={{
        payeeId: "",
        detail: "",
        name: "",
        amount: null as any
      }}
      validationSchema={Yup.object().shape({
        payeeId: Yup.string().required("Contractor is required"),
        name: Yup.string().required("Name is required"),
        amount: Yup.number().required("Amount is required"),
        detail: Yup.string().required("Details are required")
      })}
      onSubmit={async formData => {
        await createDeduction(
          {
            deductionCreate: {
              // v3 requires a CATEGORY here. v1 had no equivalent field, and
              // this is the generic "add a deduction" flow, so `Custom` is the
              // faithful mapping. (v1's `type: PrePayment` moved to
              // `applicationType`, which `DeductionCreate` does not accept —
              // v3 decides it.)
              deductionType: DeductionDeductionType.CUSTOM,
              name: formData.name,
              amount: formData.amount,
              currency: Currency.USD,
              payeeId: formData.payeeId,
              detail: formData.detail
            }
          },
          {
            onSuccess() {
              onClose();
            }
          }
        );
      }}
    >
      <WSList gap="XL">
        <WSForm.Field
          name="payeeId"
          label="Contractor"
          component={SelectPayee}
        />
        <WSForm.Field
          label="Deduction name"
          name="name"
          component={WSInputText}
        />
        <WSForm.Field
          label="Deduction amount"
          name="amount"
          component={WSInputNumber}
          componentProps={{
            currency: "USD",
            maxFractionDigits: 2,
            min: 0
          }}
        />
        {/*
          The "Start date" field is gone: v1 `IDeductionCreateRequest.startDate`
          has no v3 counterpart — neither `DeductionCreate` nor `Deduction`
          carries a start date. A one-off deduction applies to the next payable.
          See V3_MIGRATION_TRACKER.md · /v3/payments/deductions.
        */}

        <WSForm.Field
          label="Deduction details"
          name="detail"
          component={WSInputTextarea}
        />

        <ErrorAlert error={createDeductionMeta.error} context="Other" />

        <WSText color="gray500">
          Scheduled deductions are completed when you pay a contractor. If a
          deduction is larger than a payable (or your specified percentage of
          the payable) the remaining balance will be deducted from the next
          payable.
        </WSText>

        <WSForm.SubmitButton fullWidth data-testid="submit">
          Add new deduction
        </WSForm.SubmitButton>
      </WSList>
    </WSForm>
  );
};

export const useAddPayeeDeduction = () =>
  useWSModal(AddPayeeDeduction, {
    title: "Add new deduction",
    size: "S"
  });
